import { baseApi } from "./api";
import type { BugReport, CreateBugReportPayload } from "@nicsmith/shared_types";

interface BugReportListResponse {
  items: BugReport[];
  lastEvaluatedKey?: Record<string, unknown>;
}

export const bugReportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Submit a bug report
    submitBugReport: builder.mutation<BugReport, CreateBugReportPayload>({
      query: (payload) => ({
        url: "/bug-reports",
        method: "POST",
        body: payload,
      }),
    }),

    // List bug reports (admin)
    listBugReports: builder.query<BugReportListResponse, { limit?: number } | void>({
      query: (params) => ({
        url: "/bug-reports",
        params: { limit: params ? params.limit ?? 20 : 20 },
      }),
      providesTags: ["BugReports"],
    }),

    // Get single bug report (admin)
    getBugReport: builder.query<BugReport, string>({
      query: (id) => `/bug-reports/${id}`,
      providesTags: (_result, _error, id) => [{ type: "BugReports", id }],
    }),

    // Get short-lived (5 min) presigned URLs for a bug report's screenshots
    // (primary + gallery extras, in display order) (admin). "reports/*" is
    // no longer publicly readable in S3 (GDPR), so this replaces using
    // report.screenshot_url / extra_screenshot_urls directly as <img src>.
    // keepUnusedDataFor is set below the server TTL so a stale cached URL
    // (past its 5 min presigned expiry) isn't reused.
    getBugReportScreenshotUrl: builder.query<{ urls: string[]; expires_in: number }, string>({
      query: (id) => `/bug-reports/${id}/screenshot-url`,
      keepUnusedDataFor: 240,
    }),

    // Update bug report (admin)
    updateBugReport: builder.mutation<{ success: boolean }, { id: string; status?: string; admin_comments?: string }>({
      query: ({ id, ...body }) => ({
        url: `/bug-reports/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "BugReports",
        { type: "BugReports", id },
      ],
    }),

    // Create GitHub issue from bug report (admin)
    createGitHubIssue: builder.mutation<{ github_issue_url: string; github_issue_number: number }, string>({
      query: (id) => ({
        url: `/bug-reports/${id}/github`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "BugReports",
        { type: "BugReports", id },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useSubmitBugReportMutation,
  useListBugReportsQuery,
  useGetBugReportQuery,
  useGetBugReportScreenshotUrlQuery,
  useUpdateBugReportMutation,
  useCreateGitHubIssueMutation,
} = bugReportsApi;
